import { db } from '@/firebase/index.js';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  increment 
} from 'firebase/firestore';

/**
 * Creates a new pet listing in the database.
 * @param {Object} data - The pet listing data.
 * @returns {Promise<DocumentReference>} A promise resolving to the created document reference.
 */
export const createPetListing = async (data) => {
  return await addDoc(collection(db, 'pets'), data);
};

/**
 * Updates an existing pet listing.
 * @param {string} id - The document ID of the pet listing.
 * @param {Object} updateData - The fields to update.
 * @returns {Promise<void>}
 */
export const updatePetListing = async (id, updateData) => {
  return await updateDoc(doc(db, 'pets', id), updateData);
};

/**
 * Fetches a single pet listing by its ID.
 * @param {string} id - The document ID of the pet listing.
 * @returns {Promise<DocumentSnapshot>} A promise resolving to the document snapshot.
 */
export const getPetListingById = async (id) => {
  return await getDoc(doc(db, 'pets', id));
};

/**
 * Checks if a SEO URL slug already exists.
 * @param {string} slug - The slug to check.
 * @returns {Promise<boolean>} True if the slug exists, false otherwise.
 */
export const checkSlugExists = async (slug) => {
  const q = query(collection(db, 'pets'), where('slug', '==', slug));
  const querySnapshot = await getDocs(q);
  return !querySnapshot.empty;
};

/**
 * Fetches the most recently added active pet listings.
 * @param {number} limitCount - Maximum number of listings to fetch.
 * @returns {Promise<QuerySnapshot>} A promise resolving to the query snapshot.
 */
export const getRecentActivePets = async (limitCount = 8) => {
  const q = query(
    collection(db, 'pets'),
    where('status', '==', 'active'),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  return await getDocs(q);
};

/**
 * Fetches the most viewed active pet listings.
 * @param {number} limitCount - Maximum number of listings to fetch.
 * @returns {Promise<QuerySnapshot>} A promise resolving to the query snapshot.
 */
export const getPopularPets = async (limitCount = 4) => {
  const q = query(
    collection(db, 'pets'),
    where('status', '==', 'active'),
    orderBy('viewed', 'desc'),
    limit(limitCount)
  );
  return await getDocs(q);
};

/**
 * Fetches all pet listings. (Use with caution on large datasets)
 * @returns {Promise<QuerySnapshot>} A promise resolving to the query snapshot.
 */
export const getAllPets = async () => {
  return await getDocs(collection(db, 'pets'));
};

/**
 * Fetches all pet listings belonging to a specific user.
 * @param {string} userId - The UID of the user.
 * @returns {Promise<QuerySnapshot>} A promise resolving to the query snapshot.
 */
/**
 * Fetches pet listings belonging to a specific user by type (active, archived, draft).
 * @param {string} userId - The UID of the user.
 * @param {string} type - 'active', 'archived', or 'draft'
 * @returns {Promise<QuerySnapshot>} A promise resolving to the query snapshot.
 */
export const getUserListings = async (userId, type = 'active') => {
  const petsRef = collection(db, 'pets');
  // type will be 'active', 'archived', or 'draft'
  const q = query(
    petsRef,
    where('createdBy', '==', userId),
    where('status', '==', type),
    orderBy('createdAt', 'desc')
  );
  return await getDocs(q);
};

/**
 * Fetches the total views of all listings for a user.
 * @param {string} userId - The UID of the user.
 * @returns {Promise<number>} Total view count.
 */
export const getTotalPetViews = async (userId) => {
  const totalViewsQuery = query(
    collection(db, 'pets'),
    where('createdBy', '==', userId)
  );
  const snapshot = await getDocs(totalViewsQuery);
  let total = 0;
  snapshot.forEach(doc => {
    total += (doc.data().viewCount || doc.data().viewed || 0);
  });
  return total;
};

/**
 * Fetches the count of active listings for a specific user.
 * @param {string} userId - The UID of the user.
 * @returns {Promise<number>} A promise resolving to the count.
 */
export const getActiveListingsCount = async (userId) => {
  const q = query(
    collection(db, 'pets'),
    where('createdBy', '==', userId),
    where('status', '==', 'active')
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.size;
};

/**
 * Fetches the latest draft listing for a specific user.
 * @param {string} userId - The UID of the user.
 * @returns {Promise<QuerySnapshot>} A promise resolving to the query snapshot.
 */
export const getPetDraft = async (userId) => {
  const q = query(
    collection(db, 'pets'),
    where('createdBy', '==', userId),
    where('status', '==', 'draft'),
    orderBy('createdAt', 'desc'),
    limit(1)
  );
  return await getDocs(q);
};

/**
 * Increments the view count of a pet listing by 1.
 * @param {string} id - The document ID of the pet listing.
 * @returns {Promise<void>}
 */
export const incrementPetViewCount = async (id) => {
  return await updateDoc(doc(db, 'pets', id), {
    viewed: increment(1)
  });
};

/**
 * Creates and returns a Firestore query for filtering and sorting pet listings.
 * Note: Does not fetch the data, just returns the query object for pagination usage.
 * @param {Object} filters - Filter object containing category, price range, etc.
 * @returns {Query} The constructed Firestore query.
 */
export const getFilteredPetsQuery = (filters = {}) => {
  const baseFilters = [
    where('status', '==', 'active')
  ];

  // Category filter
  if (filters.category) {
    baseFilters.push(where('category', '==', filters.category));
  }

  // Sorting
  switch(filters.sort) {
    case 'mostViewed':
      return query(
        collection(db, 'pets'),
        ...baseFilters,
        orderBy('viewCount', 'desc'),
        orderBy('createdAt', 'desc')
      );
    case 'premium':
      return query(
        collection(db, 'pets'),
        ...baseFilters,
        orderBy('boosted', 'desc'),
        orderBy('createdAt', 'desc')
      );
    case 'pricelowToHigh':
      return query(
        collection(db, 'pets'),
        ...baseFilters,
        orderBy('price', 'asc'),
        orderBy('createdAt', 'desc')
      );
    case 'priceHighToLow':
      return query(
        collection(db, 'pets'),
        ...baseFilters,
        orderBy('price', 'desc'),
        orderBy('createdAt', 'desc')
      );
    case 'oldest':
      return query(
        collection(db, 'pets'),
        ...baseFilters,
        orderBy('createdAt', 'asc')
      );
    default: // newest veya undefined
      return query(
        collection(db, 'pets'),
        ...baseFilters,
        orderBy('createdAt', 'desc')
      );
  }
};
